import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Building2, Home, Car, Shield, Users, Briefcase, Lock, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab = tabParam === 'personal' ? 'personal' : 'commercial';

  const handleTabChange = (value) => {
    setSearchParams({ tab: value });
  };

  const commercialProducts = [
    { icon: Shield, slug: 'general-liability', title: 'General Liability Insurance', description: 'Protects your business from claims involving bodily injury, property damage, and advertising liability. This coverage forms the foundation of a strong risk management plan.' },
    { icon: Building2, slug: 'commercial-property', title: 'Commercial Property Insurance', description: 'Safeguards your building, equipment, inventory, and physical assets from loss due to fire, theft, storms, and other covered events.' },
    { icon: Users, slug: 'workers-compensation', title: "Workers' Compensation Insurance", description: 'Provides coverage for employee injuries or illnesses that occur on the job, helping you meet state requirements while protecting your team and your business.' },
    { icon: Car, slug: 'commercial-auto', title: 'Commercial Auto Insurance', description: 'Covers vehicles used for business purposes, including company-owned cars, vans, and trucks. Protection extends to accidents, liability, and vehicle damage.' },
    { icon: Briefcase, slug: 'professional-liability', title: 'Professional Liability (Errors and Omissions)', description: 'Designed for service-based businesses, this coverage protects against claims of negligence, errors, or failure to deliver professional services as promised.' },
    { icon: Lock, slug: 'cyber-risk', title: 'Cyber and Specialized Risk Coverage', description: 'Protects your business against data breaches, cyberattacks, and emerging risks. Additional specialty coverages are available for unique industry exposures.' }
  ];

  const personalProducts = [
    { icon: Car, slug: 'auto', title: 'Auto Insurance', description: 'Protection for you, your passengers, and your vehicle. Coverage options include liability, collision, comprehensive, and uninsured motorist protection.' },
    { icon: Home, slug: 'homeowners', title: 'Homeowners and Renters Insurance', description: 'Protects your home, personal belongings, and liability exposure. Whether you own or rent, we help you secure the place you call home.' },
    { icon: Shield, slug: 'flood', title: 'Flood Insurance', description: 'Standard home policies do not typically cover flood damage. We help you secure dedicated flood protection to safeguard your property and finances.' },
    { icon: Shield, slug: 'umbrella', title: 'Umbrella and Extended Protection Policies', description: 'Provides additional liability coverage beyond your home or auto limits. Ideal for individuals who want extra financial protection and broader peace of mind.' }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6">Our Insurance Products</h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Comprehensive coverage solutions designed to protect what matters most to you and your business.
          </p>
        </div>
      </section>

      {/* Products Tabs */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-12 h-12 bg-slate-100">
              <TabsTrigger value="commercial" className="text-lg data-[state=active]:bg-[#C9A361] data-[state=active]:text-white font-semibold">
                Commercial Insurance
              </TabsTrigger>
              <TabsTrigger value="personal" className="text-lg data-[state=active]:bg-[#C9A361] data-[state=active]:text-white font-semibold">
                Personal Insurance
              </TabsTrigger>
            </TabsList>

            {/* Commercial Insurance Tab */}
            <TabsContent value="commercial">
              <div className="mb-12 text-center">
                <h2 className="text-4xl font-bold text-slate-900 mb-4">Commercial Insurance Solutions</h2>
                <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                  No two businesses face the same risks. We offer comprehensive commercial coverage options designed to protect your operations, employees, assets, and reputation.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {commercialProducts.map((product) => {
                  const Icon = product.icon;
                  return (
                    <Link key={product.slug} to={`/products/${product.slug}`} className="block">
                      <Card className="h-full border-2 hover:border-[#C9A361] transition-all hover:shadow-xl group cursor-pointer">
                        <CardHeader>
                          <div className="bg-[#F5F1E8] w-16 h-16 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#C9A361] transition-colors">
                            <Icon className="w-8 h-8 text-[#C9A361] group-hover:text-white transition-colors" />
                          </div>
                          <CardTitle className="text-xl">{product.title}</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-slate-600 leading-relaxed mb-4">{product.description}</p>
                          <span className="inline-flex items-center gap-1 text-[#C9A361] font-semibold text-sm group-hover:gap-2 transition-all">
                            Learn more <ArrowRight className="w-4 h-4" />
                          </span>
                        </CardContent>
                      </Card>
                    </Link>
                  );
                })}
              </div>
              <div className="mt-12 text-center">
                <img
                  src="https://images.unsplash.com/photo-1762341116197-fb94a4f37173"
                  alt="Commercial office"
                  className="w-full h-96 object-cover rounded-2xl shadow-xl mb-8"
                />
                <Link to="/contact">
                  <Button size="lg" className="bg-[#C9A361] hover:bg-[#B8925A] text-white px-8 py-6 text-lg transition-all hover:scale-105">
                    Get a Commercial Insurance Quote
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </TabsContent>

            {/* Personal Insurance Tab */}
            <TabsContent value="personal">
              <div className="mb-12 text-center">
                <h2 className="text-4xl font-bold text-slate-900 mb-4">Personal Insurance Solutions</h2>
                <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                  Life brings both joyful milestones and challenging situations. Our personal insurance coverage helps you stay protected through every stage of life.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {personalProducts.map((product) => {
                  const Icon = product.icon;
                  return (
                    <Link key={product.slug} to={`/products/${product.slug}`} className="block">
                      <Card className="h-full border-2 hover:border-[#C9A361] transition-all hover:shadow-xl group cursor-pointer">
                        <CardHeader>
                          <div className="flex items-start gap-4">
                            <div className="bg-[#F5F1E8] w-14 h-14 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-[#C9A361] transition-colors">
                              <Icon className="w-7 h-7 text-[#C9A361] group-hover:text-white transition-colors" />
                            </div>
                            <div>
                              <CardTitle className="text-2xl mb-3">{product.title}</CardTitle>
                              <p className="text-slate-600 leading-relaxed mb-3">{product.description}</p>
                              <span className="inline-flex items-center gap-1 text-[#C9A361] font-semibold text-sm group-hover:gap-2 transition-all">
                                Learn more <ArrowRight className="w-4 h-4" />
                              </span>
                            </div>
                          </div>
                        </CardHeader>
                      </Card>
                    </Link>
                  );
                })}
              </div>
              <div className="mt-12 text-center">
                <img
                  src="https://images.unsplash.com/photo-1770871821382-60ea99b0a941"
                  alt="Happy family"
                  className="w-full h-96 object-cover rounded-2xl shadow-xl mb-8"
                />
                <Link to="/contact">
                  <Button size="lg" className="bg-[#C9A361] hover:bg-[#B8925A] text-white px-8 py-6 text-lg transition-all hover:scale-105">
                    Get a Personal Insurance Quote
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-slate-900 mb-6">Not Sure Which Coverage You Need?</h2>
          <p className="text-xl text-slate-600 mb-8">
            Our experienced advisors are here to help you understand your options and find the right protection for your unique situation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-[#C9A361] hover:bg-[#B8925A] text-white px-8 py-6 text-lg transition-all hover:scale-105">
                Speak With an Advisor
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:954-495-2929">
              <Button size="lg" variant="outline" className="border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white px-8 py-6 text-lg transition-all">
                Call 954-495-2929
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;
